import React, { useState } from 'react';
import classnames from 'classnames';
import styles from '../editContactModal.module.scss';
import { Form, FormControl } from 'react-bootstrap';
import { ContactFormFields, ContactFormProps } from 'v2/models';
import AvatarInput from 'v2/form/AvatarInput/AvatarInput';
import { uploadFile } from 'v2/utils/fileUtils';
import defaultCompanyAvatar from 'v2/images/defaultCompanyAvatar.png';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { useInternalDesignationOptions } from 'v2/utils/ContactUtils';
import QuestionMarkToolTip from 'v2/common/QuestionMarkToolTip/QuestionMarkToolTip';

const ContactProfileForm = ({
  register,
  getValues,
  setValue,
  contact,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const internalDesignationOptions = useInternalDesignationOptions();
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);
  const { control } = useFormContext<ContactFormFields>();
  const questionMarkToolTipHelpText =
    `Specifies if this is a ${t(TranslationKeys.contact_one)} inside` +
    ` or outside of your ${t(TranslationKeys.company_one)}.`;

  const handleAvatarChange = async (file: File | null) => {
    if (!file) {
      setValue?.('avatar_url', '');
      return;
    }

    setIsAvatarLoading(true);

    const resp = await uploadFile(file);

    setValue?.('avatar_url', resp.url);
    setIsAvatarLoading(false);
  };

  return (
    <div>
      <div className="d-flex flex-row justify-content-between">
        <Form.Group
          className={classnames('flex-column', styles.formGroup)}
          controlId="first_name"
        >
          <Form.Label>First Name</Form.Label>
          <FormControl
            type="text"
            placeholder="First Name"
            {...register?.('first_name', { required: false })}
          />
        </Form.Group>

        <Form.Group
          className={classnames('flex-column', styles.formGroup)}
          controlId="last_name"
        >
          <Form.Label>Last Name</Form.Label>
          <FormControl
            type="text"
            placeholder="Last Name"
            {...register?.('last_name', { required: false })}
          />
        </Form.Group>
      </div>
      <div className="d-flex flex-row justify-content-between">
        <Form.Group
          className={classnames('flex-column', styles.formGroup)}
          controlId="company_name"
        >
          <Form.Label>
            {t(TranslationKeys.company_one, {
              postProcess: PostProcessKeys.TitleCase,
            })}
          </Form.Label>
          {contact?.primary_associations.data_vendor ? (
            <>
              <FormControl
                type="text"
                readOnly={true}
                value={contact.primary_associations.data_vendor.name}
              />
            </>
          ) : (
            <>
              <FormControl
                type="text"
                placeholder={`${t(TranslationKeys.company_one, {
                  postProcess: PostProcessKeys.TitleCase,
                })} Name`}
                {...register?.('company_name', { required: false })}
              />
            </>
          )}
        </Form.Group>

        <Form.Group
          className={classnames('flex-column', styles.formGroup)}
          controlId="title"
        >
          <Form.Label>Title</Form.Label>
          <FormControl
            type="text"
            placeholder="Title"
            {...register?.('title', { required: false })}
          />
        </Form.Group>
      </div>
      <div className="d-flex flex-row justify-content-between">
        <Form.Group
          className={classnames('flex-column', styles.formGroup)}
          controlId="internal_designation"
        >
          <div className="d-flex align-items-baseline">
            <Form.Label>Contact Affiliation</Form.Label>
            <QuestionMarkToolTip
              styles={styles}
              helpText={questionMarkToolTipHelpText}
            />
          </div>
          <Controller
            name={'internal_designation'}
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => {
              return (
                <GeneralSelect
                  options={internalDesignationOptions}
                  selectedOption={internalDesignationOptions.find(
                    (option) => option.value === value
                  )}
                  handleChange={(option) => onChange(option?.value as boolean)}
                  placeHolder="Select Affiliation"
                  customStyles={{
                    control: {
                      width: '100%',
                      borderColor: 'var(--light-gray)',
                    },
                    dropdownIndicator: {
                      right: '10px',
                      justifyContent: 'flex-end',
                    },
                  }}
                />
              );
            }}
          />
        </Form.Group>
      </div>
      <Form.Group controlId="avatar">
        <AvatarInput
          defaultImage={defaultCompanyAvatar}
          addBtnText="Upload Photo"
          removeBtnText="Remove Photo"
          file={getValues?.().avatar_url || ''}
          onChange={handleAvatarChange}
          removeBtnVisible={true}
          className={classnames(styles.avatarInput, 'mt-2', 'form-group')}
          isLoading={isAvatarLoading}
        />
      </Form.Group>
    </div>
  );
};

export default ContactProfileForm;
